import { useState, useEffect } from 'react';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { useInfoMessages, usePortalConfig } from '@develos/next-library';
import { VehicleRecordDetailsType } from '@/types';
import { addToVehiclesList } from '@/store/liveView';
import { useDispatch, useSelector } from 'react-redux';
import { storeSelectors } from '@/store';
import { CENTRAL_API_URL_KEY } from '@/constants';
import { VehicleParametersExceedeFilterMode, VehicleWeightLimitFilterMode } from '@/store/portalSettings';

type ConfigConnection = {
  metricPointPlatformsUIds?: string[];
};

interface UseSignalRReturn {
  isConnected: boolean;
  isConnecting: boolean;
  toggleConnection: (config: ConfigConnection) => Promise<void>;
}

const useSignalR = (): UseSignalRReturn => {
  const dispatch = useDispatch();
  const { success, error } = useInfoMessages();
  const { portalUrls } = usePortalConfig(); 
  const currentLocalization = useSelector(storeSelectors.selectCurrentMetricLocalization);
  const parametersExceededFilterMode = useSelector(storeSelectors.selectParametersExceededFilterMode);
  const weightLimitFilterMode = useSelector(storeSelectors.selectWeightLimitFilterMode);
  const centralApiUrl = portalUrls[CENTRAL_API_URL_KEY];

  const [hubConnection, setHubConnection] = useState<HubConnection | null>(null);
  const [connectionState, setConnectionState] = useState<HubConnectionState>(HubConnectionState.Disconnected);

  const onReceiveNewVehicle = (records: VehicleRecordDetailsType[]) => {
    dispatch(addToVehiclesList(records));
  };

  const createHubConnection = () => {
    const connection = new HubConnectionBuilder().withUrl(`${centralApiUrl}liveVehicleRecordsHub`).build();
    connection.on('ReceiveNewVehicleData', onReceiveNewVehicle);
    connection.on('CloseConnection', async () => { await closeConnection(); });
    connection.onclose(async () => { await closeConnection(); });
    connection.onreconnected(() => {
      setConnectionState(HubConnectionState.Connected); 
    });

    setHubConnection(connection);
    setConnectionState(connection.state);

    return () => {
      connection.stop();
    };
  };

  const openConnection = async (config: ConfigConnection) => {
    if (hubConnection && connectionState === HubConnectionState.Disconnected) {
      try {
        const mode35T = weightLimitFilterMode == VehicleWeightLimitFilterMode.Above35Ton ? true : false;
        const modeAnomalies = parametersExceededFilterMode == VehicleParametersExceedeFilterMode.OnlyWithExceededParameter ? true : false;
        await hubConnection.start().then(() => {
          setConnectionState(hubConnection.state);
        }); 
        await hubConnection.invoke('RegisterPortalClient', currentLocalization!.uId, `${mode35T}`, `${modeAnomalies}`, config.metricPointPlatformsUIds);
        success('Połączenie zostało nawiązane');
      } catch (err) {
        if(hubConnection.state !== HubConnectionState.Connected) {
          error('Wystąpił błąd połączenia');
        }
        console.error(err);
      } 
    }
  };

  const closeConnection = async () => {
    if (hubConnection && connectionState === HubConnectionState.Connected) {
      setConnectionState(HubConnectionState.Disconnected);
      await hubConnection.stop();
      success('Połączenie na żywo zostało zakończone.');
    }
  };

  const toggleConnection = async (config: ConfigConnection) => {
    if (connectionState === HubConnectionState.Connected) {
      await closeConnection();
    } else {
      await openConnection(config);
    }
  };

  useEffect(() => {
    createHubConnection();

    return () => {
      if (hubConnection) {
        hubConnection.stop().catch(err => console.error('Error stopping connection:', err));
      }
    };
  }, []);

  return { 
    isConnected: connectionState === HubConnectionState.Connected, 
    isConnecting: connectionState === HubConnectionState.Connecting || connectionState === HubConnectionState.Disconnecting,
    toggleConnection 
  };
};

export default useSignalR;
